import React, { createContext, useState, useEffect, useContext } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAuthPopup, setShowAuthPopup] = useState(false);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        if (storedUser && token) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (error) {
                console.error("Failed to parse stored user", error);
                localStorage.removeItem('user');
                localStorage.removeItem('token');
            }
        } else {
            // Automatic opening of the auth popup has been disabled.
            // If you want to open it manually elsewhere, call `setShowAuthPopup(true)`.
            // (Previously there was a timer here that auto-opened the popup after 3s.)
        }
        setLoading(false);
    }, []);

    const login = async (email, password) => {
        try {
            // Backend expects 'identifier' (can be email or mobile)
            const response = await authAPI.login({ identifier: email, password });
            const { token, user } = response.data;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            setUser(user);
            setShowAuthPopup(false);
            return { success: true };
        } catch (error) {
            console.error("Login Error Context:", error);
            let errorMessage = "Login failed";
            if (error.response) {
                errorMessage = error.response.data?.error || `Server Error: ${error.response.status}`;
            } else if (error.request) {
                errorMessage = "Network Error: Cannot reach server. Check if backend is running.";
            } else {
                errorMessage = error.message;
            }

            return {
                success: false,
                message: errorMessage
            };
        }
    };

    const register = async (name, email, password, mobile, address) => {
        try {
            const response = await authAPI.register({ name, email, password, mobile, address });
            // Auto login after register or just notify? Let's auto login or ask to login.
            // For now just return success
            return { success: true };
        } catch (error) {
            console.error("Registration Error Context:", error);
            let errorMessage = "Registration failed";
            if (error.response) {
                // Server responded with a status code outside 2xx
                errorMessage = error.response.data?.error || `Server Error: ${error.response.status}`;
            } else if (error.request) {
                // Request was made but no response received
                errorMessage = "Network Error: Cannot reach server. Check if backend is running.";
            } else {
                errorMessage = error.message;
            }

            return {
                success: false,
                message: errorMessage
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        // Don't automatically show the login popup on logout.
        setShowAuthPopup(false);
    };

    return (
        <AuthContext.Provider value={{
            user,
            login,
            register,
            logout,
            loading,
            showAuthPopup,
            setShowAuthPopup
        }}>
            {children}
        </AuthContext.Provider>
    );
};
